import React, { useEffect, useMemo, useRef, useState } from 'react';
import TemplateCard from '../TemplateCard/TemplateCard';
import { dashboardUrl } from '../../config/app';
import { cachedApiRequest, getPaginatedRows } from '../../lib/api';
import {
  SAMPLE_TEMPLATES,
  buildCategoryTreeFromTemplates,
  normalizeCategory,
  normalizeTemplate,
  slugify,
} from './templateData';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './Templates.css';
import ManagedFaqSection, { pageFaqDefaults } from "../ManagedFaqSection/ManagedFaqSection";

const PRIORITY_TEMPLATE_COUNT = 6;

function categoryPath(category, subcategory) {
  const categorySlug = `${slugify(category?.name)}-${category?.id}`;
  if (!subcategory) return `/templates/category/${categorySlug}`;
  return `/templates/category/${categorySlug}/${slugify(subcategory.name)}-${subcategory.id}`;
}

function idFromSlug(value) {
  const match = String(value || '').match(/-([^-]+)$/);
  return match ? match[1] : '';
}

function preloadTemplateImages(rows, limit = PRIORITY_TEMPLATE_COUNT) {
  if (typeof document === 'undefined') return;
  rows
    .slice(0, limit)
    .map((template) => template.previewImage)
    .filter(Boolean)
    .forEach((src) => {
      const alreadyPreloaded = Array.from(document.head.querySelectorAll('link[data-template-preload]'))
        .some((link) => link.getAttribute('href') === src);
      if (alreadyPreloaded) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.setAttribute('data-template-preload', src);
      document.head.appendChild(link);
    });
}

export default function Templates({ initialData }) {
  const navigate = useNavigate();
  const { categorySlug, subcategorySlug } = useParams();
  const routeCategoryId = idFromSlug(categorySlug);
  const routeSubcategoryId = idFromSlug(subcategorySlug);
  const [templates, setTemplates] = useState(() => Array.isArray(initialData?.templates) ? initialData.templates : []);
  const [categories, setCategories] = useState(() => Array.isArray(initialData?.categories) ? initialData.categories : []);
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState(() => (
    Array.isArray(initialData?.selectedSubcategoryIds)
      ? initialData.selectedSubcategoryIds
      : (routeSubcategoryId ? [routeSubcategoryId] : [])
  ));
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => (
    Array.isArray(initialData?.expandedCategoryIds) ? initialData.expandedCategoryIds : []
  ));
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(() => initialData?.pagination || null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(() => !initialData);
  const [perPage, setPerPage] = useState(50);
  const skipInitialCategoryFetch = useRef(Boolean(initialData?.categories?.length));
  const skipInitialTemplateFetch = useRef(Boolean(initialData?.templates?.length));

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (initialData?.selectedSubcategoryIds?.length || !categories.length) return;

    if (routeSubcategoryId) {
      setSelectedSubcategoryIds([routeSubcategoryId]);
      setExpandedCategoryIds((current) => (
        routeCategoryId && !current.map(String).includes(String(routeCategoryId))
          ? [...current, routeCategoryId]
          : current
      ));
      return;
    }

    if (routeCategoryId) {
      const routeCategory = categories.find((item) => String(item.id) === String(routeCategoryId));
      if (routeCategory?.subcategories?.length) {
        setSelectedSubcategoryIds(routeCategory.subcategories.map((item) => item.id));
        setExpandedCategoryIds((current) => (
          current.map(String).includes(String(routeCategory.id)) ? current : [...current, routeCategory.id]
        ));
      }
    }
  }, [categories, initialData?.selectedSubcategoryIds, routeCategoryId, routeSubcategoryId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [page, perPage, selectedSubcategoryIds, debouncedSearch]);

  useEffect(() => {
    if (skipInitialCategoryFetch.current) {
      skipInitialCategoryFetch.current = false;
      return undefined;
    }

    let cancelled = false;

    async function loadCategories() {
      try {
        const categoryData = await cachedApiRequest('/public/email-categories', {
          cacheKey: 'public-email-categories',
        });

        if (cancelled) return;

        // The categories endpoint returns top-level categories with their
        // subcategories nested; template rows are only used as a fallback below.
        const categoryRows = categoryData
          ? getPaginatedRows(categoryData).map(normalizeCategory).filter((category) => category.name)
          : [];
        const tree = categoryRows.some((category) => category.subcategories.length) ? categoryRows : [];
        if (tree.length) {
          setCategories(tree);
          setExpandedCategoryIds(tree.filter((c) => c.subcategories.length).map((c) => c.id));
        }
      } catch {
        if (!cancelled) {
          const tree = buildCategoryTreeFromTemplates(SAMPLE_TEMPLATES);
          setCategories((current) => (current.length ? current : tree));
          setExpandedCategoryIds((current) => (current.length ? current : tree.map((c) => c.id)));
        }
      }
    }

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (skipInitialTemplateFetch.current) {
      skipInitialTemplateFetch.current = false;
      return undefined;
    }

    let cancelled = false;

    async function loadTemplates() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(perPage),
          limit: String(perPage),
        });

        if (selectedSubcategoryIds.length) {
          params.set('subcategory_ids', selectedSubcategoryIds.join(','));
        }

        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }

        const path = `/public/email-templates?${params.toString()}`;
        const templateData = await cachedApiRequest(path, {
          cacheKey: `public-email-templates-${params.toString()}`,
          cacheTtlMs: 60 * 1000,
        });

        if (cancelled) return;

        const templateRows = getPaginatedRows(templateData).slice(0, perPage).map(normalizeTemplate);
        preloadTemplateImages(templateRows);
        setTemplates(templateRows);
        const total = Number(templateData?.total || templateRows.length);
        const from = total && templateRows.length ? ((page - 1) * perPage) + 1 : 0;
        const to = total && templateRows.length ? Math.min(from + templateRows.length - 1, total) : 0;

        setPagination({
          currentPage: page,
          lastPage: Math.max(1, Math.ceil(total / perPage)),
          perPage,
          total,
          from,
          to,
        });

        setCategories((current) => {
          if (current.length) return current;
          const tree = buildCategoryTreeFromTemplates(templateRows.length ? templateRows : SAMPLE_TEMPLATES);
          setExpandedCategoryIds((expanded) => (expanded.length ? expanded : tree.map((c) => c.id)));
          return tree;
        });
      } catch {
        if (!cancelled) {
          const fallbackRows = SAMPLE_TEMPLATES;
          preloadTemplateImages(fallbackRows);
          setTemplates(fallbackRows);
          setPagination({
            currentPage: 1,
            lastPage: 1,
            perPage: fallbackRows.length,
            total: fallbackRows.length,
            from: 1,
            to: fallbackRows.length,
            prevPageUrl: null,
            nextPageUrl: null,
            links: [],
          });

          setCategories((current) => {
            if (current.length) return current;
            const tree = buildCategoryTreeFromTemplates(fallbackRows);
            setExpandedCategoryIds((expanded) => (expanded.length ? expanded : tree.map((c) => c.id)));
            return tree;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [page, perPage, selectedSubcategoryIds, debouncedSearch]);

  const toggleCategoryExpanded = (categoryId) => {
    setExpandedCategoryIds((prev) => (
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    ));
  };

  const toggleSubcategory = (category, subcategory) => {
    const subcategoryId = subcategory.id;
    setPage(1);
    setSelectedSubcategoryIds((prev) => {
      const selected = prev.map(String).includes(String(subcategoryId));
      const next = selected
        ? prev.filter((id) => String(id) !== String(subcategoryId))
        : [...prev, subcategoryId];

      if (next.length === 1) {
        navigate(categoryPath(category, subcategory));
      } else {
        navigate('/templates');
      }

      return next;
    });
  };

  const visibleTemplates = templates;

  const headingLabel = useMemo(() => {
    if (!selectedSubcategoryIds.length) return 'Template catalog';
    const selected = selectedSubcategoryIds.map(String);
    const names = categories
      .flatMap((category) => category.subcategories)
      .filter((sub) => selected.includes(String(sub.id)))
      .map((sub) => sub.name);
    return names.length ? names.join(', ') : 'Selected templates';
  }, [categories, selectedSubcategoryIds]);

  const handleEdit = (template) => {
    window.location.href = dashboardUrl(`/templates/editor/new?template_id=${encodeURIComponent(template.id)}`);
  };

  const handleClearFilters = () => {
    setSelectedSubcategoryIds([]);
    setPage(1);
  };

  const handlePaginationClick = (nextPage) => {
    if (!nextPage || nextPage === page || loading) return;
    setPage(Number(nextPage));
  };

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setPage(1);
  };

  const pageLinks = useMemo(() => {
    const lastPage = pagination?.lastPage || 1;
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }, [pagination?.lastPage]);

  const resultTotal = pagination?.total ?? visibleTemplates.length;

  return (
    <main className="templates-page">
      <section className="templates-hero">
        <span className="templates-eyebrow">Email templates</span>
        <h1>Responsive HTML Email Templates for Every Campaign</h1>
        <p>Discover professionally designed responsive HTML email templates that help you create engaging email campaigns with ease. Browse templates by category, preview every design, and customize layouts to match your brand. Whether you are creating newsletters, promotional emails, product announcements, or welcome emails, <Link className="linkcolor" to="/">Mailsnappy</Link> provides responsive email templates that are optimized for all major email clients and devices. Edit templates directly in your dashboard, personalize every section, and launch professional email campaigns faster without coding or design experience. </p>      </section>

      <section className="templates-workspace">
        <aside className="templates-sidebar">
          <h2>Filter templates</h2>
          <label className="templates-search" htmlFor="template-search">
            <span>Search</span>
            <input
              id="template-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search templates..."
            />
          </label>
          <p className="templates-result-count"><strong>{resultTotal}</strong> results</p>

          <div className="templates-category-head">
            <h3>Categories</h3>
            {selectedSubcategoryIds.length > 0 && (
              <button type="button" className="templates-clear" onClick={handleClearFilters}>
                Clear ({selectedSubcategoryIds.length})
              </button>
            )}
          </div>

          <div className="templates-category-tree">
            {categories.map((category) => {
              const isExpanded = expandedCategoryIds.includes(category.id);
              const selectedInCategory = category.subcategories
                .filter((sub) => selectedSubcategoryIds.map(String).includes(String(sub.id))).length;

              return (
                <div className="templates-category-group" key={`${category.id}-${category.name}`}>
                  <button
                    type="button"
                    className="templates-category-toggle"
                    aria-expanded={isExpanded}
                    onClick={() => toggleCategoryExpanded(category.id)}
                  >
                    <span className="templates-category-name">
                      {category.name}
                      {selectedInCategory > 0 && <em className="templates-category-badge">{selectedInCategory}</em>}
                    </span>
                    <svg
                      className={`templates-chevron ${isExpanded ? 'open' : ''}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    >
                      <path d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                    </svg>
                  </button>

                  {isExpanded && (
                    category.subcategories.length ? (
                      <div className="templates-subcategory-list">
                        {category.subcategories.map((sub) => {
                          const checked = selectedSubcategoryIds.map(String).includes(String(sub.id));
                          return (
                            <label className={`templates-subcategory ${checked ? 'checked' : ''}`} key={sub.id}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSubcategory(category, sub)}
                              />
                              <span>{sub.name}</span>
                              <small>{sub.templatesCount || 0}</small>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="templates-subcategory-empty">No subcategories yet.</p>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <div className="templates-content">
          <div className="templates-toolbar">
            <div>
              <h2>{headingLabel}</h2>
              <p>
                {loading
                  ? 'Loading templates...'
                  : `Showing ${pagination?.from || 0}-${pagination?.to || visibleTemplates.length} of ${resultTotal} templates`}
              </p>
            </div>
            <div className="templates-toolbar-actions">
              <label className="templates-per-page" htmlFor="templates-per-page">
                <span>Preview</span>
                <select
                  id="templates-per-page"
                  value={perPage}
                  onChange={handlePerPageChange}
                  disabled={loading}
                >
                  <option value={50}>50 templates</option>
                  <option value={100}>100 templates</option>
                  <option value={200}>200 templates</option>
                </select>
              </label>
              {selectedSubcategoryIds.length > 0 && (
                <button type="button" className="templates-toolbar-clear" onClick={handleClearFilters}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="templates-grid">
              {Array.from({ length: Math.min(perPage, 12) }).map((_, index) => <div className="template-skeleton" key={index} />)}
            </div>
          ) : visibleTemplates.length ? (
            <div className="templates-grid">
              {visibleTemplates.map((template, index) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleEdit}
                  priority={index < PRIORITY_TEMPLATE_COUNT}
                />
              ))}
            </div>
          ) : (
            <div className="templates-empty">No templates found.</div>
          )}

          {!loading && pagination?.lastPage > 1 && (
            <nav className="templates-pagination" aria-label="Template pages">
              <button
                type="button"
                disabled={pagination.currentPage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>

              {pageLinks.map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={pagination.currentPage === pageNumber ? 'active' : ''}
                  aria-current={pagination.currentPage === pageNumber ? 'page' : undefined}
                  onClick={() => handlePaginationClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}

              <button
                type="button"
                disabled={pagination.currentPage >= pagination.lastPage}
                onClick={() => setPage((current) => Math.min(pagination.lastPage, current + 1))}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </section>

      <ManagedFaqSection
        pageKey="templates"
        heading="Email template FAQs"
        fallback={pageFaqDefaults.templates}
      />

    </main>
  );
}
